'use client'

import { useRef, useCallback } from 'react'
import { Maximize } from 'lucide-react'
import { TransformWrapper, TransformComponent, useControls, ReactZoomPanPinchRef } from 'react-zoom-pan-pinch'
import {
  Dialog,
  DialogContent,
  DialogTitle,
} from '@/components/ui/dialog'

interface MediaPreviewProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  // Either SVG content or image URL
  svgContent?: string
  imageUrl?: string
  title?: string
}

// Fit to Screen button that uses stored scale
function FitButton({ fitScale }: { fitScale: React.MutableRefObject<number> }) {
  const { centerView } = useControls()

  return (
    <button
      onClick={() => centerView(fitScale.current)}
      className="p-2 hover:bg-zinc-200 dark:hover:bg-zinc-700 rounded transition-colors"
      title="Fit to screen"
      aria-label="Fit to screen"
    >
      <Maximize className="h-4 w-4" />
    </button>
  )
}

export function MediaPreview({
  open,
  onOpenChange,
  svgContent,
  imageUrl,
  title = 'Preview'
}: MediaPreviewProps) {
  const fitScaleRef = useRef(1)
  const transformRef = useRef<ReactZoomPanPinchRef>(null)

  // Calculate optimal scale to fit content within viewport
  const calculateFitScale = useCallback((ref: ReactZoomPanPinchRef) => {
    const wrapper = ref.instance.wrapperComponent
    const content = ref.instance.contentComponent
    if (!wrapper || !content) return 1

    // Find the content element (SVG or IMG)
    const contentElement = content.querySelector('svg') || content.querySelector('img')
    if (!contentElement) return 1

    // Get actual dimensions
    const contentRect = contentElement.getBoundingClientRect()
    const wrapperRect = wrapper.getBoundingClientRect()

    // Calculate scale to fit with padding
    const padding = 64
    const availableWidth = wrapperRect.width - padding
    const availableHeight = wrapperRect.height - padding

    const scaleX = availableWidth / contentRect.width
    const scaleY = availableHeight / contentRect.height
    return Math.min(scaleX, scaleY, 1) // Don't scale up beyond 1
  }, [])

  const handleInit = useCallback((ref: ReactZoomPanPinchRef) => {
    transformRef.current = ref
    // Calculate and apply optimal scale after DOM is ready
    // Use double requestAnimationFrame to ensure layout is complete
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        const scale = calculateFitScale(ref)
        fitScaleRef.current = scale
        ref.centerView(scale)
      })
    })
  }, [calculateFitScale])

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-[95vw] max-h-[95vh] w-[95vw] h-[90vh] md:w-screen md:h-screen md:max-w-none md:max-h-none md:rounded-none p-0 gap-0 overflow-hidden flex flex-col"
        showCloseButton={true}
        onOpenAutoFocus={(e) => e.preventDefault()}
        onWheel={(e) => {
          // Prevent browser zoom on Ctrl+wheel (trackpad pinch)
          if (e.ctrlKey) {
            e.preventDefault()
          }
        }}
      >
        <DialogTitle className="sr-only">{title}</DialogTitle>

        <TransformWrapper
          initialScale={1}
          minScale={0.1}
          maxScale={10}
          centerOnInit={true}
          limitToBounds={false}
          alignmentAnimation={{ disabled: true }}
          wheel={{ smoothStep: 0.01 }}
          pinch={{ disabled: false }}
          panning={{ velocityDisabled: false }}
          onInit={handleInit}
        >
          <div className="flex items-center justify-between px-4 py-2 border-b bg-background/95 backdrop-blur shrink-0">
            <FitButton fitScale={fitScaleRef} />
          </div>

          <TransformComponent
            wrapperStyle={{
              width: '100%',
              height: '100%',
              flex: 1,
              touchAction: 'none', // Prevent browser zoom on pinch
            }}
            wrapperClass="bg-zinc-100 dark:bg-zinc-900"
            contentStyle={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '32px',
            }}
          >
            {svgContent ? (
              <div dangerouslySetInnerHTML={{ __html: svgContent }} />
            ) : imageUrl ? (
              <img
                src={imageUrl}
                alt={title}
                className="max-w-none"
                style={{ imageRendering: 'auto' }}
              />
            ) : null}
          </TransformComponent>
        </TransformWrapper>
      </DialogContent>
    </Dialog>
  )
}
